<!--
	@component
	address form (city/zip/street/house number) with interdependent autocomplete fields.
	uses AutocompleteInputField with `valueAsObject: true`.

	@param {AddressData} addressData - bindable. the address object written back on each field change.
	@param {string} label - required. heading above the address grid (e.g. "Ausführungsort").
	@param {FontProps} fontProps - required. font styling for all fields.
	@param {string} errorPath - required. base path for form validation errors.
	@param {boolean} isEditable - controls disabled state of all fields.
	@param {string} outerId - identity key — `{#key outerId}` re-mounts fields when a different entity is selected.
	@param {boolean} showAnonymizeCheckbox - shows the "Adresse anonymisieren" checkbox.
	@param {() => void} onChange - callback fired after address fields sync to addressData.

	fetch strategy: `config.fetch` is set once by `getFormAcConfigFetch` with a `[query]` placeholder URL.
	svelecte's internal debouncedFetch replaces `[query]` with user input on type-to-search.
	cross-field filtering (zip/street params) is NOT baked into the fetch URL — it caused
	svelecte's watch_fetch_init to re-fire on re-render, aborting user queries.

	@see PersonAndCompanyDataAutocomplete for the person/company counterpart.
	@see AutocompleteInputField for the underlying svelecte wrapper.
-->
<script lang="ts">
	import AutocompleteInputField from './AutocompleteInputField.svelte'
	import { writable, type Writable } from 'svelte/store'
	import { getContext, hasContext, tick } from 'svelte'
	import { DefaultRobotoFontProps, type FontProps } from '$types/fontProps'
	import type { AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import { getFormAcConfigFetch } from '$types/autocompleteFieldConfig'
	import { t } from '$i18n/i18n'
	import { inputFieldFormats } from '$utils/formatters'
	import InputField from '../molecules/InputField.svelte'
	import {
		type AddressData,
		addressSchema,
		copyAddressData,
		DefaultAddressData,
		COORDINATE_PATTERN,
		STREET_NUMBER_PATTERN_WITH_SPACE
	} from '$types/addressData'
	import { getFormErrors } from '$utils/formValidation'
	import { clearFormErrors } from '../map/fak/utils/fakUtils'
	import { fetchUtils } from '$utils/fetchUtils'
	import Icon from '../atoms/Icon.svelte'
	import { createTippy } from 'svelte-tippy'
	import copyIcon from '@/icons/icon_copy_02.svg'
	import Checkbox from '../atoms/Checkbox.svelte'
	import infoIcon from '@/icons/icon_info_green_circle_outline.svg'

	let {
		addressData = $bindable(),
		label,
		fontProps,
		required = false,
		errorPath,
		isEditable = false,
		outerId = '', // used to detect if another lot or project is selected
		idPrefix = '',
		showAnonymizeCheckbox = false,
		onChange = undefined
	}: {
		addressData: AddressData
		label: string
		fontProps: FontProps
		required?: boolean
		errorPath: string
		isEditable?: boolean
		outerId?: string
		idPrefix?: string
		showAnonymizeCheckbox?: boolean
		onChange?: (() => void) | undefined
	} = $props()
	const checkboxLabelFontProps: FontProps = DefaultRobotoFontProps(
		'1.25rem',
		'0.875rem',
		'#9d9d9d',
		'#5E6468'
	)

	let formErrors: Writable<[{ path: string; message: string }]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([{ path: '', message: '' }])

	// these track addressData for initial/reset values, but are overridden by svelecte via bind:value.
	// using $state with manual sync in autocompleteValueChanged to avoid $derived infinite loops
	// (a $derived would create new object references each eval, triggering svelecte onChange → addressData write → re-derive → loop).
	// id field required for svelecte's fetch_initValue computation — without it,
	// value['id'] is undefined → requestFactory can't replace [query] → literal qry=[query] → 400
	let city: { value: string; text: string } | undefined = $state(
		addressData?.city ? { value: addressData.city, text: addressData.city } : undefined
	)
	let street: { value: string; text: string } | undefined = $state(
		getStreetName() ? { value: getStreetName()!, text: getStreetName()! } : undefined
	)
	let streetNumber: string | null = $state(getHouseNumber())
	let zip: { value: string; text: string } | undefined = $state(
		addressData?.zip ? { value: addressData.zip, text: addressData.zip } : undefined
	)
	// Workaround: `bind:checked={addressData.anonymize}` triggers `binding_property_non_reactive`
	// because Checkbox.svelte is Svelte 4 and can't bind to a nested property of a $bindable() prop.
	// Local $state + manual sync — same pattern as city/street/zip/streetNumber above.
	// Remove when src/lib/atoms/Checkbox.svelte is migrated to Svelte 5 (13 consumers, needs its own PR).
	let anonymize: boolean = $state(addressData?.anonymize ?? false)
	let cityOptions: { value: string; text: string }[] = $state(city ? [city] : [])
	let streetOptions: { value: string; text: string }[] = $state(street ? [street] : [])
	let zipOptions: { value: string; text: string }[] = $state(zip ? [zip] : [])

	function createAcConfig(type: string): AutocompleteFieldConfig {
		const config = getFormAcConfigFetch(type, false, 'value', 'text', !isEditable, false)
		setConfig(config)
		return config
	}

	// $state makes fetch URL mutations reactive — Svelecte sees the new URL on each field change.
	// Cross-filter params are appended imperatively in the field-specific onChange handlers below,
	// never via $derived, to avoid watch_fetch_init re-firing during active user queries.
	let cityAutocompleteConfig: AutocompleteFieldConfig = $state(createAcConfig('city-data'))
	cityAutocompleteConfig.fetchCallback = deduplicateCityOptions
	cityAutocompleteConfig.searchProps = { skipSort: false }
	let zipAutocompleteConfig: AutocompleteFieldConfig = $state(createAcConfig('zip-data'))
	let streetAutocompleteConfig: AutocompleteFieldConfig = $state(createAcConfig('street-data'))

	const BASE_CITY_FETCH = '/api/city-data/autocomplete/svelte?qry=[query]'
	const BASE_ZIP_FETCH = '/api/zip-data/autocomplete/svelte?qry=[query]'
	const BASE_STREET_FETCH = '/api/street-data/autocomplete/svelte?qry=[query]'

	function updateCityFetch(zipVal: string, streetName: string) {
		let url = BASE_CITY_FETCH
		if (zipVal) url += `&zip=${encodeURIComponent(zipVal)}`
		if (streetName) url += `&street=${encodeURIComponent(streetName)}`
		cityAutocompleteConfig.fetch = url
	}

	function updateZipFetch(cityName: string, streetName: string) {
		let url = BASE_ZIP_FETCH
		if (cityName) url += `&city=${encodeURIComponent(cityName)}`
		if (streetName) url += `&street=${encodeURIComponent(streetName)}`
		zipAutocompleteConfig.fetch = url
	}

	function updateStreetFetch(cityName: string, zipVal: string) {
		let url = BASE_STREET_FETCH
		if (cityName) url += `&city=${encodeURIComponent(cityName)}`
		if (zipVal) url += `&zip=${encodeURIComponent(zipVal)}`
		streetAutocompleteConfig.fetch = url
	}

	async function onCityChange() {
		const cityName = extractCityName(city?.text ?? '')
		const zipVal = zip?.text ?? ''
		const streetName = street?.text ?? ''
		updateZipFetch(cityName, streetName)
		updateStreetFetch(cityName, zipVal)
		await autocompleteValueChanged()
	}

	async function onZipChange() {
		const cityName = extractCityName(city?.text ?? '')
		const zipVal = zip?.text ?? ''
		const streetName = street?.text ?? ''
		updateCityFetch(zipVal, streetName)
		updateStreetFetch(cityName, zipVal)
		await autocompleteValueChanged()
	}

	async function onStreetChange() {
		const cityName = extractCityName(city?.text ?? '')
		const zipVal = zip?.text ?? ''
		const streetName = street?.text ?? ''
		updateCityFetch(zipVal, streetName)
		updateZipFetch(cityName, streetName)
		await autocompleteValueChanged()
	}

	// Be careful setting config values later from stores (in this case, placeholder $t store), since the execution order
	// is not guaranteed here. In this case, this lead to a problem where the placeholder was loaded a little bit later
	// in the AutocompleteInputField component. The placeholder was set as label there, marked as required but was indeed
	// null at this point in time.
	function setConfig(config: AutocompleteFieldConfig) {
		config.creatable = true
		config.creatablePrefix = ''
		config.valueAsObject = true
		config.delimiter = ';'
		config.strictMode = false
	}

	function onAnonymizeChange() {
		addressData.anonymize = anonymize
		autocompleteValueChanged()
	}

	async function autocompleteValueChanged() {
		await tick()
		// pre-populate dropdown options for empty fields based on other filled fields.
		// safe to call here because onChange fires on SELECTION, not during typing.
		await preFetchOptions()
		await tick()

		if (!addressData) {
			addressData = DefaultAddressData()
		}
		// only write changed values to avoid triggering reactive loops via $bindable
		const newStreet = getFullStreet()
		const newZip = zip?.text ?? ''
		const newCity = extractCityName(city?.text ?? '')
		if (addressData.street !== newStreet) addressData.street = newStreet
		if (addressData.zip !== newZip) addressData.zip = newZip
		if (addressData.city !== newCity) addressData.city = newCity
		addressData.anonymize = showAnonymizeCheckbox && anonymize

		// This is a quick fix, until we have the street/houseNumber also as its own field in the database a.k.a., when
		// we got rid of Sirius.
		// Just for validation
		if (streetNumber) {
			addressData.houseNumber = streetNumber
		} else {
			addressData.houseNumber = undefined
		}

		clearFormErrors()

		if (city && zip && street) {
			try {
				addressSchema.validateSync(copyAddressData(addressData), { abortEarly: false })
			} catch (error) {
				$formErrors = getFormErrors(error, errorPath + '.')
				return
			}
		}
		// Reset after validation
		addressData.houseNumber = undefined

		onChange?.()
	}

	async function preFetchOptions() {
		if (!city) {
			await fetchUtils
				.get(
					'/api/city-data/autocomplete/svelte?qry=&zip=' +
						(zip?.text ?? '') +
						'&street=' +
						(street?.text ?? ''),
					false
				)
				.then((data) => (cityOptions = deduplicateCityOptions(data)))
				.catch()
		}
		if (!zip) {
			await fetchUtils
				.get(
					'/api/zip-data/autocomplete/svelte?qry=&city=' +
						(city?.text ?? '') +
						'&street=' +
						(street?.text ?? ''),
					false
				)
				.then((data) => (zipOptions = data))
				.catch()
		}
		if (!street) {
			await fetchUtils
				.get(
					'/api/street-data/autocomplete/svelte?qry=&zip=' +
						(zip?.text ?? '') +
						'&city=' +
						(city?.text ?? ''),
					false
				)
				.then((data) => (streetOptions = data))
				.catch()
		}
	}

	function getFullStreet() {
		let tmpStreet = street?.text ?? ''
		let tmpNumber = streetNumber ? ' ' + streetNumber : ''
		if (COORDINATE_PATTERN.test(tmpStreet)) {
			return tmpStreet
		}
		return tmpStreet ? tmpStreet + tmpNumber : ''
	}

	function getStreetName() {
		if (addressData && addressData.street) {
			if (COORDINATE_PATTERN.test(addressData.street)) {
				const match = addressData.street.match(COORDINATE_PATTERN)
				return match ? match[0] : null
			}

			const streetNumberMatcher = addressData.street.trim().match(STREET_NUMBER_PATTERN_WITH_SPACE)
			if (streetNumberMatcher) {
				return addressData.street
					.substring(
						0,
						streetNumberMatcher.index! + streetNumberMatcher[0].indexOf(streetNumberMatcher[1])
					)
					.trim()
			}
			return addressData.street
		}
		return null
	}

	function getHouseNumber() {
		if (addressData && addressData.houseNumber) {
			return addressData.houseNumber
		}
		if (addressData && addressData.street) {
			if (COORDINATE_PATTERN.test(addressData.street)) {
				return null
			}
			// Matches street numbers: single (e.g., "123", "42A") or ranges (e.g., "1-2", "5a-6b", "7c - 8d")
			const match = addressData.street.match(STREET_NUMBER_PATTERN_WITH_SPACE)
			return match ? match[0] : null
		}
		return null
	}

	function extractCityName(text: string): string {
		const commaIdx = text.indexOf(',')
		return commaIdx >= 0 ? text.slice(0, commaIdx).trim() : text.trim()
	}

	function deduplicateCityOptions(
		response: { data: { value: string; text: string }[] } | { value: string; text: string }[]
	): { value: string; text: string }[] {
		const options = Array.isArray(response) ? response : (response.data ?? [])
		const seen = new Set<string>()
		return options
			.filter((opt) => {
				const key = extractCityName(opt.text)
				if (seen.has(key)) return false
				seen.add(key)
				return true
			})
			.sort((a, b) =>
				extractCityName(a.text.replace(/[^a-zA-Z0-9äöüÄÖÜß ]/g, '')).localeCompare(
					extractCityName(b.text.replace(/[^a-zA-Z0-9äöüÄÖÜß ]/g, '')),
					'de'
				)
			)
	}

	const cityRenderer = (item: object): string =>
		extractCityName(String((item as Record<string, unknown>).text ?? ''))

	function copyAddressToClipboard() {
		let houseNumberString = addressData?.houseNumber ? ` ${addressData?.houseNumber}` : ''
		let addressString = `${addressData?.street}${houseNumberString}, ${addressData?.zip} ${addressData?.city}`
		navigator.clipboard.writeText(addressString)
	}

	const copyTippy = createTippy({
		animation: 'scale-extreme',
		content: $t('UI.addressData.copy'),
		delay: [100, 200],
		hideOnClick: true,
		interactive: true,
		maxWidth: 367,
		placement: 'left',
		theme: 'mmttip',
		appendTo: document.getElementsByClassName('fak-base-layout')[0]
	})

	const anonymizeTippy = createTippy({
		animation: 'scale-extreme',
		content: $t('AddressData.anonymize.tooltip'),
		delay: [100, 200],
		hideOnClick: true,
		interactive: true,
		maxWidth: 367,
		placement: 'left',
		theme: 'mmttip',
		appendTo: document.getElementsByClassName('fak-base-layout')[0]
	})

	let disabled = $derived(!isEditable)
</script>

<div class="address-container">
	<div class="address-header">
		<h4>{label}</h4>
		<Icon src={copyIcon} clickable on:click={copyAddressToClipboard} tippy={copyTippy} />
	</div>
	{#if showAnonymizeCheckbox && addressData}
		<div class="checkbox-anonymize-container">
			<Checkbox
				bind:checked={anonymize}
				id="anonymize-address-checkbox"
				size="1.25rem"
				labelFontProps={checkboxLabelFontProps}
				label={$t('AddressData.anonymize')}
				{disabled}
				on:checkboxChange={onAnonymizeChange}
			/>
			<Icon src={infoIcon} size={24} tippy={anonymizeTippy} />
		</div>
	{/if}
	<div class="address-grid">
		<!-- re-mount fields when outerId changes (different entity selected), reinitializing $state from addressData -->
		{#key outerId}
			<div style="grid-area: city">
				<AutocompleteInputField
					bind:value={city}
					options={cityOptions}
					id="{idPrefix}-input-city"
					{disabled}
					{required}
					{fontProps}
					label={$t('UI.map.place.ph')}
					autocompleteConfig={cityAutocompleteConfig}
					errorPath={errorPath + '.city'}
					disableCreateRowLabel
					renderer={cityRenderer}
					onChange={onCityChange}
				></AutocompleteInputField>
			</div>
			<div style="grid-area: zip">
				<AutocompleteInputField
					bind:value={zip}
					options={zipOptions}
					id="{idPrefix}-input-zip"
					{disabled}
					{required}
					{fontProps}
					label={$t('UI.map.zip.ph')}
					autocompleteConfig={zipAutocompleteConfig}
					errorPath={errorPath + '.zip'}
					disableCreateRowLabel
					onChange={onZipChange}
				></AutocompleteInputField>
			</div>
			<div style="grid-area: street">
				<AutocompleteInputField
					bind:value={street}
					options={streetOptions}
					id="{idPrefix}-input-street"
					{disabled}
					{required}
					{fontProps}
					autocompleteConfig={streetAutocompleteConfig}
					errorPath={errorPath + '.street'}
					label={$t('UI.map.streetOrCoordinates.ph')}
					disableCreateRowLabel
					onChange={onStreetChange}
				></AutocompleteInputField>
			</div>
			<div style="grid-area: street-number">
				<InputField
					bind:value={streetNumber}
					id="{idPrefix}-input-street-number"
					{fontProps}
					readOnly={disabled}
					format={inputFieldFormats.FULL}
					placeholder={$t('AddressData.street.number')}
					errorPath={errorPath + '.houseNumber'}
					on:change={autocompleteValueChanged}
				></InputField>
			</div>
		{/key}
	</div>
</div>

<style lang="scss">
	@import '$styles/global';
	@import '$styles/autocomplete';

	.address-container {
		@include flex-col(0.5rem, $alignment: flex-start);

		width: 100%;
	}

	.address-header {
		@include flex-row($justify: space-between);

		width: 100%;
	}

	.address-grid {
		display: grid;
		grid-template-columns: 2fr 1fr;
		grid-template-areas:
			'city zip'
			'street street-number';
		gap: 0.938rem; // same as other fields on the FAK
		width: 100%;

		& > div {
			width: 100%;
		}
	}

	.checkbox-anonymize-container {
		@include flex-row($col-gap: 0.25rem);
	}

	h4 {
		margin: 0;
	}
</style>
